/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { connect } from 'react-redux'

import Feature from '@modules/utils/feature'

import {
  fetchOrganization as fetchOrganizationAction,
  fetchOrganizations,
  fetchOrganizationsByEntitlementId,
  resetFetchOrganizationRequest,
  resetFetchOrganizationsRequest,
} from '@/actions/organizations'
import { isFeatureActivated } from '@/selectors'
import {
  searchStackDeploymentsRequest,
  fetchOrganizationsRequest,
  fetchOrganizationRequest,
} from '@/reducers/asyncRequests/registry'
import { getStackDeploymentsFromSearch } from '@/reducers/stackDeploymentSearches'
import { getOrganizations, getBillingOrganizationsByEntitlementId } from '@/reducers/organizations'
import { getDeploymentByIdQuery } from '@/lib/deploymentQuery'
import { searchDeployments } from '@/actions/stackDeployments'
import { resetSearchStackDeploymentsRequest } from '@/actions/stackDeployments/crud'

import Organizations from './Organizations'

import type { ThunkDispatch, ReduxState } from '@/types/redux'
import type { StateProps, DispatchProps, ConsumerProps } from './types'

const queryId = `search-organization-by-deployment-id`

const mapStateToProps = (state: ReduxState): StateProps => ({
  organizations: getOrganizations(state),
  deployments: getStackDeploymentsFromSearch(state, queryId)?.deployments || [],
  billingOrganizationsByEntitlementId: getBillingOrganizationsByEntitlementId(state),
  searchOrganizationByIdRequest: (organizationId) =>
    fetchOrganizationRequest(state, organizationId),
  searchOrganizationsByNameRequest: fetchOrganizationsRequest(state),
  searchOrganizationByDeploymentIdRequest: searchStackDeploymentsRequest(state, queryId),
  searchOrganizationByEntitlementIdRequest: fetchOrganizationsRequest(state),
  resetSearchOrganizationByIdRequest: (organizationId: string) =>
    resetFetchOrganizationRequest(organizationId),
  resetSearchOrganizationsByNameRequest: () => resetFetchOrganizationsRequest(),
  resetSearchOrganizationByDeploymentIdRequest: () => resetSearchStackDeploymentsRequest(queryId),
  resetSearchOrganizationByEntitlementIdRequest: () => resetFetchOrganizationsRequest(),
  lookupSaasUsers: isFeatureActivated(state, Feature.lookupSaasUsers),
})

const mapDispatchToProps = (dispatch: ThunkDispatch): DispatchProps => ({
  searchOrganizationById: (organizationId: string) =>
    dispatch(fetchOrganizationAction({ organizationId })),
  searchOrganizationsByName: (organizationName: string) =>
    dispatch(fetchOrganizations({ organizationName })),
  searchOrganizationByDeploymentId: (deploymentId: string) =>
    dispatch(searchDeployments({ queryId, query: getDeploymentByIdQuery({ deploymentId }) })),
  searchOrganizationByEntitlementId: (entitlementId: string) =>
    dispatch(fetchOrganizationsByEntitlementId({ entitlementId })),
  fetchOrganization: (organizationId: string) =>
    dispatch(fetchOrganizationAction({ organizationId })),
})

export default connect<StateProps, DispatchProps, ConsumerProps>(
  mapStateToProps,
  mapDispatchToProps,
)(Organizations)
